import { useMemo } from "react";
import { fromTimestamp } from "@racoon/lib";
import { startOfDay } from "date-fns";
import { ReportJob, useCompletedJobs } from "./use-report-data";
import { DateValue } from "~/shared/lib/analytics-utils";
import { containerVolume, idKey } from "./job-report";
import { useReportFilterValues } from "./use-report-filters";

/** One row of «Универсальный отчёт по вывозу» (AIS 15.1.3): one crew's collections of one container type at a pickup location on one day. */
export interface UniversalReportRow {
  /** Day, pickup location, container type, truck and driver, which identify the row. */
  key: string;
  /** Local day the jobs were completed. */
  date: Date | null;
  /** Shown as the row's ID. */
  pickupLocationId: bigint;
  serviceReceiverId: bigint | null;
  serviceReceiverName: string;
  serviceReceiverBin: string;
  pickupLocationName: string;
  containerTypeName: string;
  /** Containers installed, summed over the distinct container groups of the row's jobs. */
  numberOfContainers: number | null;
  containerVolume: number | null;
  /** Actual volumes of the row's jobs, summed; null when none was declared. */
  factTotalVolume: number | null;
  /** Completed jobs in the row. */
  collections: number;
  truckId: bigint | null;
  truckPlateNumber: string;
  driverId: bigint | null;
  /** "Surname Name". */
  driverName: string;
}

const byDateSiteTypeCrew = (a: UniversalReportRow, b: UniversalReportRow): number =>
  (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0) ||
  a.pickupLocationName.localeCompare(b.pickupLocationName) ||
  a.containerTypeName.localeCompare(b.containerTypeName) ||
  a.truckPlateNumber.localeCompare(b.truckPlateNumber) ||
  a.driverName.localeCompare(b.driverName);

/** Completed jobs grouped by completion day, pickup location, container type, truck and driver. */
export function universalReportRows(
  jobs: ReportJob[],
  containerType: string,
): UniversalReportRow[] {
  // Each row keeps the container groups it has counted: several jobs on one group in a
  // day service the same containers, so the group's count must not add up per job.
  const entries = new Map<string, { row: UniversalReportRow; countedGroupIds: Set<string> }>();

  for (const job of jobs) {
    const typeId = idKey(job.containerTypeId);
    if (containerType && typeId !== containerType) continue;

    const factEndAt = fromTimestamp(job.factEndAt);
    const date = factEndAt ? startOfDay(factEndAt) : null;
    // Truck and driver are in the key so each row names one of each.
    const key = [
      date?.getTime() ?? "",
      idKey(job.pickupLocationId),
      typeId,
      idKey(job.truckId),
      idKey(job.driverId),
    ].join("|");

    let entry = entries.get(key);
    if (!entry) {
      // A pickup location belongs to one service receiver, so the row's first job names it.
      entry = {
        row: {
          key,
          date,
          pickupLocationId: job.pickupLocationId,
          serviceReceiverId: job.serviceReceiverId,
          serviceReceiverName: job.serviceReceiverName ?? "",
          serviceReceiverBin: job.serviceReceiverBin ?? "",
          pickupLocationName: job.pickupLocationName ?? "",
          containerTypeName: job.containerTypeName ?? "",
          numberOfContainers: null,
          containerVolume: null,
          factTotalVolume: null,
          collections: 0,
          truckId: job.truckId ?? null,
          truckPlateNumber: job.truckPlateNumber ?? "",
          driverId: job.driverId ?? null,
          driverName: job.driverName ?? "",
        },
        countedGroupIds: new Set(),
      };
      entries.set(key, entry);
    }
    const { row, countedGroupIds } = entry;

    row.collections += 1;
    if (job.factTotalVolume != null) {
      row.factTotalVolume = (row.factTotalVolume ?? 0) + job.factTotalVolume;
    }

    // Legacy jobs predate container groups and carry no count.
    const groupId = idKey(job.containerGroupId);
    const count = job.numberOfContainers ?? null;
    if (count != null && groupId && !countedGroupIds.has(groupId)) {
      countedGroupIds.add(groupId);
      row.numberOfContainers = (row.numberOfContainers ?? 0) + count;
      row.containerVolume = (row.containerVolume ?? 0) + (containerVolume(job) ?? 0);
    }
  }

  return [...entries.values()].map((e) => e.row).sort(byDateSiteTypeCrew);
}

/** The report's rows for the period and the filters in the URL. */
export function useUniversalReportRows(startDate: DateValue, endDate: DateValue) {
  const { serviceReceiver, pickupLocation, containerType, truck } = useReportFilterValues();
  const {
    rows: jobs,
    isLoading,
    error,
  } = useCompletedJobs(startDate, endDate, { serviceReceiver, pickupLocation, truck });

  const rows = useMemo(() => universalReportRows(jobs, containerType), [jobs, containerType]);

  return { rows, isLoading, error };
}
